import React, { createContext, useContext, useState, ReactNode, useCallback, useMemo } from 'react';
import type { ToastProps, ToastOptions } from './Toast';
import Toast, { ToastContainer } from './Toast';

export interface ToastContextType {
  success: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  error: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  warning: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  info: (message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) => string;
  notification:any
}

export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastProps[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((options: ToastOptions & { variant: ToastProps['variant'] }) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast: ToastProps = {
      id,
      ...options,
      variant: options.variant ?? 'info',
      onClose: removeToast,
    };
    setToasts(prev => [...prev, newToast]);
    return id;
  }, [removeToast]);

  const success = useCallback((message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) =>
    addToast({ message, variant: 'success', ...options }), [addToast]);

  const error = useCallback((message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) =>
    addToast({ message, variant: 'error', ...options }), [addToast]);

  const warning = useCallback((message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) =>
    addToast({ message, variant: 'warning', ...options }), [addToast]);

  const info = useCallback((message: string, options?: Omit<ToastOptions, 'message' | 'variant'>) =>
    addToast({ message, variant: 'info', ...options }), [addToast]);

  const notification = useCallback((
    title: string,
    message: string,
    options?: Omit<ToastOptions, 'message' | 'variant' | 'title'>
  ) =>
    addToast({
      title,
      message,
      variant: 'notification',
      ...options,
    }), [addToast]);

  const contextValue = useMemo(() => ({
    success, error, warning, info, notification
  }), [success, error, warning, info, notification]);
  
  return (
    <ToastContext.Provider value={contextValue}>
      <ToastContainer toasts={toasts} onClose={removeToast} position="top-right" />
      {children}
    </ToastContext.Provider>
  );
};

